'use client';

// P29.7 marketing "Come funziona" 3-step section. Client Component due to
// framer-motion whileInView triggers. Stagger fade-in once per card; honors
// useReducedMotion. Uses `m` for LazyMotion compatibility.

import { m, useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { UserPlus, SlidersHorizontal, Sparkles } from 'lucide-react';

const STEPS = [
  { key: 'step1' as const, Icon: UserPlus },
  { key: 'step2' as const, Icon: SlidersHorizontal },
  { key: 'step3' as const, Icon: Sparkles },
];

export function HowItWorks() {
  const t = useTranslations('marketing.howItWorks');
  const reduceMotion = useReducedMotion();

  const initial = reduceMotion ? false : { opacity: 0, y: 20 };
  const whileInView = { opacity: 1, y: 0 };

  return (
    <section id="how-it-works" data-testid="marketing-how-it-works" className="bg-white py-20">
      <div className="mx-auto max-w-6xl px-4">
        <div className="text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
            {t('sectionTitle')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-600 md:text-base">
            {t('sectionSubtitle')}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
          {STEPS.map((step, idx) => {
            const Icon = step.Icon;
            return (
              <m.div
                key={step.key}
                data-testid={`how-it-works-${step.key}`}
                initial={initial}
                whileInView={whileInView}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.45, delay: idx * 0.1 }}
                className="relative rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="text-xs font-semibold tracking-widest text-emerald-700 uppercase">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  {t(`${step.key}.title`)}
                </h3>
                <p className="mt-2 text-sm text-slate-600">{t(`${step.key}.description`)}</p>
              </m.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
